import React from "react";
import { cn } from "../../styles";
import { ButtonVariant } from "../../atoms/Button/ButtonBase";
import { ThemeColor, PanelColor, BaseElementProps } from "../../props";


export interface ButtonGroupProps
  extends BaseElementProps,
    Omit<React.HTMLAttributes<HTMLDivElement>, "color"> {
  /**
   * Button elements to group together.
   */
  children: React.ReactNode;

  /**
   * Shared thematic color applied to all child buttons unless overridden per-button.
   * @default ThemeColor.gray
   */
  color?: ThemeColor | PanelColor;

  /**
   * Layout direction of the button group.
   * @default "horizontal"
   */
  orientation?: "horizontal" | "vertical";
}

/**
 * Groups multiple Button components into a visually connected unit.
 * Handles border-radius and border collapsing automatically based on position.
 * Pass `variant`, `color`, and `dense` here to apply them uniformly;
 * any prop set directly on a child Button takes precedence.
 */
export const ButtonGroup = ({
  children,
  className,
  color = ThemeColor.gray,
  orientation = "horizontal",
  dense = false,
  ...props
}: ButtonGroupProps) => {
  const childArray = React.Children.toArray(children).filter(
    React.isValidElement,
  );
  const count = childArray.length;
  const isHorizontal = orientation === "horizontal";

  return (
    <div
      className={cn("inline-flex", isHorizontal ? "flex-row" : "flex-col", className)}
      {...props}
    >
      {childArray.map((child, index) => {
        const isFirst = index === 0;
        const isLast = index === count - 1;
        const isOnly = count === 1;

        let radiusClass = "";
        if (!isOnly) {
          if (isHorizontal) {
            if (isFirst) radiusClass = "rounded-r-none";
            else if (isLast) radiusClass = "rounded-l-none";
            else radiusClass = "rounded-none";
          } else {
            if (isFirst) radiusClass = "rounded-b-none";
            else if (isLast) radiusClass = "rounded-t-none";
            else radiusClass = "rounded-none";
          }
        }

        const typedChild = child as React.ReactElement<any>;

        // Collapse adjacent borders so we don't get double-width seams
        let borderClass = "";
        const childVariant = typedChild.props.variant ?? ButtonVariant.outlined;
        if (childVariant === ButtonVariant.outlined && !isOnly && !isLast) {
          borderClass = isHorizontal ? "border-r-0" : "border-b-0";
        }

        return React.cloneElement(typedChild, {
          color: typedChild.props.color ?? color,
          dense: typedChild.props.dense ?? dense,
          className: cn(radiusClass, borderClass, typedChild.props.className),
          key: index,
        });
      })}
    </div>
  );
};

ButtonGroup.displayName = "ButtonGroup";
